import { useState, useEffect, useMemo } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';

import CallsWindow from './windows/calls/CallsWindow';
import { DoubleBoardWindow } from './windows/scoreboard/Double';
import { SingleBoardWindow } from './windows/scoreboard/Single';
import CallError from './windows/errors/CallError';
import {
  AlertEnable,
  FotoWindow,
  SettingsSIP,
  Settings,
  SettingsLimits,
  Denominations2,
  RackWindow,
  SignalsWindow,
  SettingsReceipt,
} from 'app/windows';
import { Counter } from './counters/counter';
import { WindowVisibleController } from './windows-controller.styled';

// const scale = 1;

export const WindowsController = ({ scale }) => {
  const { t } = useTranslation();
  const windows = useSelector((store) => store.windows.openedWindows, shallowEqual);
  const newWindow = useSelector((store) => store.windows.newWindow, shallowEqual);
  const withCounters = user.haveRight('client_counters.list');
  const counters = useSelector((store) => store.counters.counters, shallowEqual);
  // const sumRegular = useSelector((store) => store.counters.sum, shallowEqual);
  const sumOneTime = useSelector((store) => store.boards.sum, shallowEqual);
  const sumRegular = useSelector((store) => store.boards.sumRegular, shallowEqual);
  const onetime = useSelector((store) => store.windows.onetime);
  const regular = useSelector((store) => store.windows.regular);

  /**
   * get counters array with onetime and regular
   * @returns
   */
  const getCounters = () => {
    const regularAll = {
      client_id: 'regularAll',
      cl: 'common',
      client_name: t('widgets.Постоянные клиенты'),
      items: Object.keys(sumRegular).map((zone_id) => {
        const val = sumRegular[zone_id];
        return {
          zone_id,
          zone_name: zone_id,
          limit: val.limit,
          maximum: val.maximum,
          free: val.free,
        };
      }),
    };

    const oneTimeAll = {
      client_id: 'oneTimeAll',
      client_name: t('widgets.Разовые клиенты'),
      cl: 'common',
      items: Object.keys(sumOneTime).map((zone_id) => {
        const val = sumOneTime[zone_id];
        return {
          zone_id,
          zone_name: zone_id,
          limit: val.limit,
          maximum: val.maximum,
          free: val.free,
        };
      }),
    };

    if (!withCounters) {
      return [];
    }
    const out = [];
    if (onetime && oneTimeAll.items.length) {
      out.push(oneTimeAll);
    }
    if (regular && regularAll.items.length) {
      out.push(regularAll);
    }
    return [...out, ...counters];
  };

  // get all counters
  const countersOk = getCounters();

  // get counters obj
  const countersObj = useMemo(() => {
    const out = {};
    countersOk.forEach((el) => (out[el.client_id] = el));
    return out;
  }, [countersOk]);

  // get counters string
  const countersString = useMemo(() => {
    let out = '';
    countersOk.forEach((el) => (out += ' ' + el.client_id));
    return out;
  }, [countersOk]);

  // fill order array with initial counters
  const [order, setOrder] = useState(countersOk.map((el) => el.client_id));

  // change counters order if counters change
  useEffect(() => {
    // console.log('🚀 ~ WindowsController ~ countersString:', countersString);

    // old order
    const out = [...order];
    // delete old
    order.forEach((id) => {
      // if id not from window - delete it from counters
      if (windows[id] === undefined) {
        // find idx with id
        const idx = out.findIndex((el) => el === id);
        if (idx !== -1) {
          out.splice(idx, 1);
        }
      }
    });

    // add new
    const outOk = [...countersOk.map((el) => el.client_id), ...out];
    // console.log('🚀 ~ WindowsController ~ outOk:', outOk);

    setOrder(outOk);
  }, [countersString]);

  useEffect(() => {
    if (order.indexOf(newWindow) === -1) {
      const newOrder = [...order];
      newOrder.push(newWindow);
      setOrder(newOrder);
    }
  }, [newWindow]);

  /**
   * set active window
   * @param {*} id
   */
  const setActive = (id) => {
    let newOrder = [...order];
    newOrder = newOrder.filter((el) => el !== id);
    newOrder.push(id);
    setOrder(newOrder);
  };

  /**
   * get window by type
   * @param {*} type
   * @returns
   */
  const getWindow = (type) => {
    const coord = {};
    let Window = null;
    switch (type) {
      case 'foto':
        Window = FotoWindow;
        break;
      case 'calls':
        Window = CallsWindow;
        break;
      case 'rack':
        Window = RackWindow;
        break;
      case 'signals':
        Window = SignalsWindow;
        break;
      case 'denominations2':
        Window = Denominations2;
        break;
      case 'double_board':
        Window = DoubleBoardWindow;
        break;
      case 'single_board':
        Window = SingleBoardWindow;
        break;
      case 'settings':
        Window = Settings;
        coord.defaultPosition = { x: 500, y: 0 };
        break;
      case 'settingssip':
        Window = SettingsSIP;
        coord.defaultPosition = { x: 500, y: 0 };
        break;
      case 'settingslimits':
        Window = SettingsLimits;
        coord.defaultPosition = { x: 500, y: 0 };
        break;
      case 'settingsreceipt':
        Window = SettingsReceipt;
        coord.defaultPosition = { x: 100, y: 100 };
        break;
      case 'alert_enable':
        Window = AlertEnable;
        coord.defaultPosition = { x: 500, y: 0 };
        break;

      case 'call_error':
        Window = CallError;
        break;
      default:
        throw new Error('Unknown window type: ' + type);
    }
    return { Window, coord };
  };

  /**
   * get windows
   * @returns
   */
  const getWindows = () => {
    const out = [];
    let cidx = 0;
    order.forEach((id) => {
      let item = windows[id];
      if (!item) {
        // counter
        if (countersObj[id]) {
          item = countersObj[id];
          const idxok = cidx;
          // console.log("🚀 ~ order.forEach ~ idxok:", idxok)
          const cl = item.cl;
          out.push(
            <WindowVisibleController key={'win_ev_' + id} onClick={() => setActive(id)} scale={1}>
              <Counter data={item} key={idxok} index={idxok} scale={1} className={cl} />
            </WindowVisibleController>
          );
          cidx++;
        }

        //this.removeFromOrder(id)
      } else {
        const { Window, coord } = getWindow(item.type);
        out.push(
          <WindowVisibleController key={'win_ev_' + id} onClick={() => setActive(item.id)} scale={scale}>
            <Window data={item} {...coord} scale={scale} />
          </WindowVisibleController>
        );
      }
    });
    return out;
  };

  const windowsEl = getWindows();

  /**
   * get counters
   * @returns
   */
  // const getCounters = () => {
  //   const out = [];
  //   const countersOk = [regularAll, oneTimeAll, ...counters];
  //   let idxok;
  //   let cl = '';
  //   countersOk?.forEach((el, idx) => {
  //     idxok = idx; // + 2;
  //     cl = idx < 2 ? 'common' : '';
  //     out.push(<Counter data={el} key={idxok} index={idxok} scale={scale} className={cl} />);
  //   });
  //   return out;
  // };

  // const countersEl = getCounters();

  return (
    <div>
      {windowsEl}
      {/* {countersEl} */}
    </div>
  );
};
